import { Vue, Component, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { debounce as _debounce } from 'lodash';

import sitesClient from '@/core/api/sites-service';
import { SiteCustomerListItemDTO, ISiteCustomerListItemDTO } from '@/../client-generator/generated-clients/sites';
import { UserProfileDTO } from '@/../client-generator/generated-clients/user';
import { LOADING_CONTENT } from '@/core/store/types/action-types';

import SitesListCardsComponent from './SitesListCardsComponent/SitesListCardsComponent.vue';
import SitesListTableComponent from './SitesListTableComponent/SitesListTableComponent.vue';

@Component({
  components: {
    SitesListCardsComponent,
    SitesListTableComponent,
  },
  computed: {
    ...mapGetters(['profile']),
  },
  i18n: {
    messages: {
      it: {
        customerNotActive: 'Cliente non attivo',
        customerNotFound: 'Cliente non trovato',
      },
    },
  },
})
export default class SitesList extends Vue {
  isLoading = false;

  profile!: UserProfileDTO | null;
  allSites: SiteCustomerListItemDTO[] = [];
  groupedSites: Map<string, SiteCustomerListItemDTO[]> = new Map();

  ss = '';
  searchString = '';

  orderBy: any = null;
  viewType = 'card';

  get sitesNumber() {
    return this.filteredSites.length;
  }

  get orderByOptions() {
    return [
      {
        value: 'siteName',
        markup: `<span class="mr-1">${this.$t('orderBy')}:</span>
        <strong>${this.$t('name')}</strong>`,
        label: `${this.$t('orderBy')}: ${this.$t('name')}`,
      },
      {
        value: 'lastService',
        markup: `<span class="mr-1">${this.$t('orderBy')}:</span>
        <strong>${this.$t('lastService')}</strong>`,
        label: `${this.$t('orderBy')}: ${this.$t('lastService')}`,
      },
      {
        value: 'jobTaskId',
        markup: `<span class="mr-1">${this.$t('orderBy')}:</span>
        <strong>${this.$t('siteFilterOrderByJobTask')}</strong>`,
        label: `${this.$t('orderBy')}: ${this.$t('siteFilterOrderByJobTask')}`,
      },
    ];
  }

  get filteredSites() {
    if (!this.allSites || this.allSites.length === 0) {
      return [];
    }

    let list = this.allSites;

    if (this.searchString) {
      const ssLowercase = this.searchString.toLowerCase();

      list = this.allSites.filter((site) => {
        return site.siteName?.toLowerCase().includes(ssLowercase) || site.address?.toLowerCase().includes(ssLowercase);
      });
    }

    list = list.sort(this.compareSites);

    return list;
  }
  get filteredGroupedSites() {
    if (!this.groupedSites || this.groupedSites.size === 0) {
      return new Map();
    }

    const filteredGroupedList: Map<string, SiteCustomerListItemDTO[]> = new Map();
    this.groupedSites.forEach((value: SiteCustomerListItemDTO[], key: string) => {
      if (this.searchString) {
        const ssLowercase = this.searchString.toLowerCase();
        value = value.filter((site) => {
          return (
            site.siteName?.toLowerCase().includes(ssLowercase) ||
            site.address?.toLowerCase().includes(ssLowercase) ||
            site.city?.toLowerCase().includes(ssLowercase) ||
            site.jobTaskDescription?.toLowerCase().includes(ssLowercase)
          );
        });
      }
      value = value.sort(this.compareSites);
      filteredGroupedList.set(key, value);
    });
    return Array.from(filteredGroupedList);
  }

  get showLastServed() {
    if (this.orderBy) {
      return this.orderBy.value === 'lastService';
    }

    return false;
  }

  @Watch('viewType')
  viewTypeUpdated() {
    localStorage.setItem(`${this.$route.name}_viewType`, this.viewType);
  }

  created() {
    this.handleSearch = _debounce(this.handleSearch, 500);
    this.orderBy = this.orderByOptions[0];
  }

  mounted(): void {
    this.loadSites();
    this.viewType = localStorage.getItem(`${this.$route.name}_viewType`) || 'card';
  }

  async loadSites() {
    this.$store.dispatch(LOADING_CONTENT, true);

    try {
      const result = await sitesClient.myList('');
      this.allSites = result.data as SiteCustomerListItemDTO[];
      this.groupedSites = this.groupBy(this.allSites, (site: SiteCustomerListItemDTO) => site.jobTaskId);
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 204:
          messageError = this.$t('customerNotActive').toString();
          break;
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('customerNotFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  isViewType(viewType: string) {
    return this.viewType === viewType;
  }

  handleSearch(val) {
    this.searchString = val;
  }

  compareSites(a: ISiteCustomerListItemDTO, b: ISiteCustomerListItemDTO) {
    const property = this.orderBy.value;

    let ap = a[property];
    let bp = b[property];

    if (property === 'siteName') {
      ap = ap.toLowerCase();
      bp = bp.toLowerCase();

      if (ap < bp) {
        return -1;
      }

      if (ap > bp) {
        return 1;
      }
    }

    if (property === 'lastService') {
      ap = new Date(ap);
      bp = new Date(bp);

      if (ap > bp) {
        return -1;
      }

      if (ap < bp) {
        return 1;
      }
    }

    return 0;
  }

  groupBy(list: any[], keyGetter: { (obj: any): any; (arg0: any): any }): Map<any, any> {
    const map = new Map();
    list.forEach((item: any) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  getTaskGroupName(taskId) {
    return this.allSites.find((site) => site.jobTaskId === taskId)?.jobTaskDescription;
  }
}
